import i18n, { type BackendModule, type ReadCallback } from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./en";

export type SupportedUiLang = "ru" | "en" | "ko";

export const SUPPORTED_UI_LANGUAGES: {
  id: SupportedUiLang;
  label: string;
  flag: string;
}[] = [
  { id: "ru", label: "Русский", flag: "🇷🇺" },
  { id: "en", label: "English", flag: "🇬🇧" },
  { id: "ko", label: "한국어", flag: "🇰🇷" },
];

/**
 * Detect the best UI language. Maps ru-* and ko-* variants explicitly and
 * uses English for unsupported languages. Pass an explicit language tag
 * (e.g. Telegram's `language_code`)
 * to take priority over the browser's; omit it to fall back to
 * `navigator.language`.
 */
export function detectBrowserLang(code?: string | null): SupportedUiLang {
  const tag =
    code ??
    (typeof navigator !== "undefined" ? (navigator.language ?? "") : "");
  const base = tag.toLowerCase().split(/[-_]/)[0];
  if (base === "ru" || base === "ko") return base;
  return "en";
}

/** Locales fetched as their own chunk on first use, keeping them out of the
    entry bundle. English is bundled instead of listed here: it is the
    fallback, so i18next loads it on every start anyway. */
const LAZY_LOCALES: Record<string, () => Promise<{ default: object }>> = {
  ru: () => import("./ru"),
  ko: () => import("./ko"),
};

const lazyLocaleBackend: BackendModule = {
  type: "backend",
  init: () => {},
  read(language: string, _namespace: string, callback: ReadCallback) {
    const load = LAZY_LOCALES[language];
    // Region variants (ko-KR) and unsupported tags resolve further down the
    // fallback chain, so an empty bundle is the right answer for them.
    if (!load) {
      callback(null, {});
      return;
    }
    void load().then(
      (module) => callback(null, module.default),
      (error: unknown) => callback(error as Error, false),
    );
  },
};

i18n
  .use(lazyLocaleBackend)
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en } },
    // Mix the bundled English with the lazily fetched locales.
    partialBundledLanguages: true,
    // Match the host/browser on the first paint; the persisted setting takes
    // over from the store after mount.
    lng: detectBrowserLang(),
    // English is the neutral emergency fallback for malformed/dynamic keys.
    // Shipped locales are structurally checked, so normal ko UI never reaches it.
    fallbackLng: "en",
    interpolation: {
      escapeValue: false,
    },
    // No Suspense boundary wraps the app chrome; until the locale chunk lands,
    // components render the bundled English instead of throwing.
    react: {
      useSuspense: false,
    },
  });

export default i18n;
